
import React, { useEffect } from 'react';
import { Motion } from '@/components/ui/motion';
import { Check, AlertCircle, Loader2, Clock, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useCollectionForm } from '../context/CollectionFormContext';
import { cn } from '@/lib/utils';

export type TransactionStatus = 'loading' | 'success' | 'error' | 'auth_success' | 'processing';

type StatusDisplayProps = {
  status: TransactionStatus;
  amount: number;
  reason: string;
  courierName?: string;
  isAuthRequest?: boolean;
  transactionId?: string;
  message?: string;
  onReset: () => void;
  onClose: () => void;
};

const statusConfig = {
  loading: {
    icon: Loader2,
    title: 'Выполняется запрос',
    description: 'Отправка запроса на обработку платежа...',
    color: 'text-blue-500',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-500',
    progress: 35,
  },
  processing: {
    icon: Clock,
    title: 'Обработка запроса',
    description: 'Обработка платежного запроса...',
    color: 'text-blue-500',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-500',
    progress: 65,
  },
  success: {
    icon: Check,
    title: 'Списание выполнено',
    description: 'Платеж успешно обработан',
    color: 'text-green-500',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-500',
    progress: 100,
  },
  auth_success: {
    icon: CreditCard,
    title: 'Авторизация выполнена',
    description: 'Средства успешно заблокированы',
    color: 'text-green-500',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-500',
    progress: 100,
  },
  error: {
    icon: AlertCircle,
    title: 'Ошибка обработки',
    description: 'Не удалось выполнить платеж',
    color: 'text-red-500',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-500',
    progress: 100,
  },
};

export const CollectionStatusDisplay: React.FC<StatusDisplayProps> = ({
  status,
  amount,
  reason,
  courierName = 'Курьер',
  isAuthRequest = false,
  transactionId,
  message,
  onReset,
  onClose,
}) => {
  const config = statusConfig[status];
  const Icon = config.icon;

  // Эффект для автоматического продвижения статуса с loading на processing
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (status === 'loading') {
      timeout = setTimeout(() => {
        // В реальном приложении здесь будет обновление статуса с сервера
      }, 1500);
    }
    return () => clearTimeout(timeout);
  }, [status]);

  // Определяем тип операции для заголовка информации
  const operationType = isAuthRequest ? 'Авторизация' : 'Взыскание';

  return (
    <div className="space-y-6 py-4 animate-fade-in">
      {/* Карточка с информацией о транзакции */}
      <Card className={cn(
        "overflow-hidden transition-all duration-500",
        "border-2 shadow-lg",
        status === 'loading' || status === 'processing' ? "border-blue-400 animate-glow" : 
        status === 'success' || status === 'auth_success' ? "border-green-400" : 
        "border-red-400"
      )}>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4">
            {/* Статус - иконка и заголовок */}
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-medium">{operationType}</h3>
              <div
                className={cn(
                  "flex items-center justify-center w-10 h-10 rounded-full",
                  config.bgColor
                )}
              >
                {status === 'loading' || status === 'processing' ? (
                  <Loader2 className={cn("h-5 w-5 animate-spin", config.color)} />
                ) : (
                  <Icon className={cn("h-5 w-5", config.color)} />
                )}
              </div>
            </div>

            {/* Прогресс бар */}
            <div className="w-full space-y-2">
              <div className="flex justify-between text-sm font-medium">
                <span className={config.color}>{config.title}</span>
                <span>{config.progress}%</span>
              </div>
              <Motion defaultStyle={{ progress: 0 }} style={{ progress: { val: config.progress, stiffness: 170, damping: 26 } }}>
                {interpolated => (
                  <Progress 
                    value={interpolated.progress} 
                    className={cn(
                      "h-2",
                      status === 'loading' || status === 'processing' ? "bg-blue-100" : 
                      status === 'success' || status === 'auth_success' ? "bg-green-100" : 
                      "bg-red-100"
                    )}
                    indicatorClassName={cn(
                      status === 'loading' || status === 'processing' ? "bg-blue-500" : 
                      status === 'success' || status === 'auth_success' ? "bg-green-500" : 
                      "bg-red-500"
                    )}
                  />
                )}
              </Motion>
            </div>

            {/* Информация о транзакции */}
            <div className="mt-2 space-y-3 pt-3 border-t border-border/30">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Курьер:</span>
                <span className="font-medium">{courierName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Сумма:</span>
                <span className="font-medium">{amount.toLocaleString()} ₽</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Операция:</span>
                <span className="font-medium">{isAuthRequest ? 'Авторизация карты' : 'Списание средств'}</span>
              </div>
              {transactionId && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ID транзакции:</span>
                  <span className="font-medium text-sm break-all">{transactionId}</span>
                </div>
              )}
              <div className="pt-2">
                <span className="text-muted-foreground block mb-1">Комментарий:</span>
                <span className="font-medium block text-sm py-2 px-3 bg-muted rounded-md">{reason}</span>
              </div>
            </div>

            {/* Сообщение от сервера */}
            {message && (
              <div className={cn(
                "mt-2 p-3 rounded-md text-sm",
                status === 'error' ? "bg-red-50 text-red-700" :
                status === 'success' || status === 'auth_success' ? "bg-green-50 text-green-700" :
                "bg-blue-50 text-blue-700"
              )}>
                {message}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Кнопки действий */}
      <div className="flex justify-end gap-3 mt-4">
        {status === 'error' && (
          <Button onClick={onReset} className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-rotate-ccw">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
            Повторить
          </Button>
        )}
        <Button onClick={onClose} variant={status === 'error' ? "outline" : "default"}>
          {status === 'success' || status === 'auth_success' ? 'Закрыть' : 'Назад'}
        </Button>
      </div>
    </div>
  );
};
